import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Card, CardContent, Typography, Grid,
  Paper, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, useTheme
} from '@mui/material';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, BarChart, Bar
} from 'recharts';
import { getApiKeys, getKeyUsageStats, setApiKey, setApiEndpoint } from '../../api/services/agent-studio/AgentFeatures';

const ApiUsage = ({ agentId, apiKey, endpoint }) => {
  const theme = useTheme();
  const [usageData, setUsageData] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(0);
  const usageFetchState = useRef({
    signature: '',
    timestamp: 0,
    inFlight: false
  });

  useEffect(() => {
    let cancelled = false;
    if (apiKey) {
      setApiKey(apiKey);
      if (endpoint) {
        setApiEndpoint(endpoint);
      }
      fetchAllUsageData(cancelled);
    } else {
      setError('No API key available for this agent');
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [apiKey, endpoint]);

  const fetchAllUsageData = async (cancelled) => {
    let signature = '';
    try {
      if (!apiKey) {
        throw new Error('API key is missing');
      }

      signature = `${apiKey}-${endpoint}`;
      const now = Date.now();
      if (usageFetchState.current.inFlight) {
        return;
      }
      if (usageFetchState.current.signature === signature && now - usageFetchState.current.timestamp < 60000) {
        return;
      }
      usageFetchState.current = { signature, timestamp: now, inFlight: true };
      const keysResponse = await getApiKeys(apiKey, endpoint);
      const apiKeys = keysResponse.data.keys;
      
      const usagePromises = apiKeys.map(key => 
        getKeyUsageStats(key.id, apiKey, endpoint)
      );
      
      const usageResults = await Promise.all(usagePromises);
      
      const keyUsageData = apiKeys.map((key, index) => ({
        id: key.id,
        name: key.name || `API Key ${key.id.slice(-4)}`,
        role: key.role,
        total_requests: usageResults[index].data.total_requests,
        total_tokens: usageResults[index].data.total_tokens,
        last_request_at: usageResults[index].data.last_request_at,
        usage_history: usageResults[index].data.usage_history
      }));
      
      if (!cancelled) {
        setUsageData(keyUsageData);
        usageFetchState.current = { signature, timestamp: Date.now(), inFlight: false };
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching usage data:', err);
    } finally {
      usageFetchState.current = { ...usageFetchState.current, inFlight: false };
      if (!cancelled) {
        setLoading(false);
      }
      if (cancelled) {
        usageFetchState.current = { signature: '', timestamp: 0, inFlight: false };
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: 'success.main' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading API usage data: {error}</Typography>
      </Box>
    );
  }

  const formatKeyLabel = (keyData) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="subtitle2" sx={{ color: 'success.main' }}>
          {keyData.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ID: {keyData.id}
        </Typography>
      </Box>
    );
  };

  const ChartView = () => {
    const getTruncatedId = (id) => {
      return `...${id.slice(-4)}`;
    };

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total Requests</Typography>
              <Typography variant="h3" sx={{ color: 'success.main' }}>
                {usageData.reduce((sum, key) => sum + (key.total_requests || 0), 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total Tokens</Typography>
              <Typography variant="h3" sx={{ color: 'success.main' }}>
                {usageData.reduce((sum, key) => sum + (key.total_tokens || 0), 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Requests by API Key</Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={usageData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 40
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ 
                        fontSize: 12,
                        fill: theme.palette.success.main
                      }}
                      label={{ 
                        value: 'API Key Name',
                        position: 'bottom',
                        offset: 20
                      }}
                    />
                    <YAxis 
                      label={{ 
                        value: 'Number of Requests',
                        angle: -90,
                        position: 'insideLeft',
                        offset: 10
                      }}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Box sx={{ 
                              bgcolor: 'background.paper', 
                              p: 1.5, 
                              border: '1px solid',
                              borderColor: 'success.main',
                              borderRadius: 1
                            }}>
                              <Typography sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
                                {data.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                ID: {data.id}
                              </Typography>
                              <Typography>
                                Requests: {data.total_requests?.toLocaleString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Last used: {formatDateTime(data.last_request_at)}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar 
                      dataKey="total_requests" 
                      fill={theme.palette.success.main} 
                      name="Requests"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {usageData.map((keyData) => (
          keyData.usage_history && (
            <Grid item xs={12} key={keyData.id}>
              <Card sx={{ bgcolor: 'background.paper' }}>
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'success.main' }}>
                      {keyData.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {keyData.role ? `Role: ${keyData.role} • ` : ''}ID: {keyData.id}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={keyData.usage_history}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 20
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="timestamp" />
                        <YAxis />
                        <Tooltip 
                          contentStyle={{
                            backgroundColor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'success.main',
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="requests" 
                          stroke={theme.palette.success.main} 
                          name="Requests"
                          dot={{ fill: theme.palette.success.main }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    );
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const ListView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>API Key ID</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right">Total Requests</TableCell>
            <TableCell align="right">Total Tokens</TableCell>
            <TableCell align="right">Last Used</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {usageData.map((key) => (
            <TableRow key={key.id}>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {key.name}
              </TableCell>
              <TableCell sx={{ color: 'success.main', fontFamily: 'monospace' }}>
                {key.id}
              </TableCell>
              <TableCell>{key.role || 'N/A'}</TableCell>
              <TableCell align="right">{key.total_requests?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">{key.total_tokens?.toLocaleString() || 0}</TableCell>
              <TableCell align="right">
                {formatDateTime(key.last_request_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>API Usage Statistics</Typography>
      <Tabs 
        value={view} 
        onChange={(_, newValue) => setView(newValue)}
        sx={{ 
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.7)',
            '&.Mui-selected': { color: 'success.main' }
          },
          '& .MuiTabs-indicator': { backgroundColor: 'success.main' }
        }}
      >
        <Tab label="CHARTS" />
        <Tab label="LIST" />
      </Tabs>

      {view === 0 ? <ChartView /> : <ListView />}
    </Box>
  );
};

export default ApiUsage;
